import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useTabLeader } from './useTabLeader';
import { toast } from 'sonner';

export interface IncomingRing {
  id: string;
  contact_id?: string;
  lead_id?: string;
  deal_id?: string;
  requested_by: string;
  current_level: number;
  call_id: string;
  metadata?: any;
  timeout_seconds: number;
  sdr_name?: string;
  contact_name?: string;
  lead_name?: string;
  title?: string;
}

// Cooldown period after rejecting a ring (60 seconds)
const REJECTION_COOLDOWN_MS = 60000;

export interface ActiveDailyCall {
  roomUrl: string;
  token: string;
  callId: string;
  meetingId?: string;
  clientJoinUrl?: string;
}

export function useVideoRingNotifications() {
  const [incomingRing, setIncomingRing] = useState<IncomingRing | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeDailyCall, setActiveDailyCall] = useState<ActiveDailyCall | null>(null);
  const notificationRef = useRef<Notification | null>(null);
  const incomingRingRef = useRef<IncomingRing | null>(null);
  const isProcessingRef = useRef(false);
  const appUserIdRef = useRef<string | null>(null);
  // Local list of dismissed ring IDs - persists across re-renders
  const dismissedRingIdsRef = useRef<Set<string>>(new Set());
  const { user } = useAuth();
  
  // Leader election - only the leader tab will process rings
  // Store last isLeader value to detect changes
  const wasLeaderRef = useRef(false);
  const { isLeader, tabId } = useTabLeader('video-ring-leader');

  // Keep ref in sync with state to avoid stale closure in Realtime callback
  useEffect(() => {
    incomingRingRef.current = incomingRing;
  }, [incomingRing]);

  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((perm) => {
        console.log('[VideoRing] 🔔 Notification permission:', perm);
      });
    }
  }, []);

  // Stable stopRinging function - no dependencies
  const stopRinging = useCallback(() => {
    console.log('[VideoRing] 🛑 Stopping ring sound and clearing state');
    // Stop programmatic audio
    if (ringIntervalRef.current) {
      clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = null;
    }
    // Close browser notification
    if (notificationRef.current) {
      notificationRef.current.close();
      notificationRef.current = null;
    }
    setIncomingRing(null);
  }, []);

  // A1) Close immediately if we lose leadership (multi-tab)
  useEffect(() => {
    // Only act when we LOSE leadership (was leader, now not)
    if (wasLeaderRef.current && !isLeader) {
      console.log('[VideoRing] 👑 Lost leadership, closing modal and stopping sound');
      stopRinging();
    }
    wasLeaderRef.current = isLeader;
  }, [isLeader, stopRinging]);

  // A2) Watchdog: while modal is open, poll backend status every 3s
  useEffect(() => {
    if (!incomingRing) return;

    const watchdogInterval = setInterval(async () => {
      try {
        const { data, error } = await supabase
          .from('video_call_ring_requests')
          .select('status, ended_at, updated_at, created_at')
          .eq('id', incomingRing.id)
          .single();

        if (error || !data) {
          console.log('[VideoRing] ⚠️ Watchdog: ring not found, closing modal');
          stopRinging();
          return;
        }

        // If status changed from ringing, close
        if (data.status !== 'ringing') {
          console.log('[VideoRing] ⚠️ Watchdog: status changed to', data.status, '- closing modal');
          stopRinging();
          return;
        }

        // If ring is too old (>5 min from created_at), close
        const ringAgeMs = Date.now() - new Date(data.created_at).getTime();
        if (ringAgeMs > 5 * 60 * 1000) {
          console.log('[VideoRing] ⚠️ Watchdog: ring too old, closing modal');
          stopRinging();
        }
      } catch (err) {
        console.error('[VideoRing] Watchdog error:', err);
      }
    }, 10000); // Reduced from 3s to 10s to decrease server load

    return () => clearInterval(watchdogInterval);
  }, [incomingRing?.id, stopRinging]);

  // Programmatic ring sound using Web Audio API (no external file needed)
  const audioContextRef = useRef<AudioContext | null>(null);
  const ringIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const playRingSound = useCallback(() => {
    // Stop any existing ring first
    if (ringIntervalRef.current) return;

    const playBeep = () => {
      try {
        const ctx = audioContextRef.current || new AudioContext();
        audioContextRef.current = ctx;

        // Double beep pattern (like a phone ring)
        const playTone = (startTime: number, duration: number, frequency: number) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = frequency;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0.3, startTime);
          gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
          osc.start(startTime);
          osc.stop(startTime + duration);
        };

        const now = ctx.currentTime;
        // First beep
        playTone(now, 0.15, 880);
        // Second beep (after short pause)
        playTone(now + 0.2, 0.15, 880);
      } catch (err) {
        console.error('[VideoRing] Failed to play beep:', err);
      }
    };

    // Play immediately and repeat every 2 seconds
    playBeep();
    ringIntervalRef.current = setInterval(playBeep, 2000);
    console.log('[VideoRing] 🔊 Ring sound started (Web Audio API)');
  }, []);

  const stopRingSound = useCallback(() => {
    if (ringIntervalRef.current) {
      clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = null;
      console.log('[VideoRing] 🔇 Ring sound stopped');
    }
  }, []);

  // Stable acceptRing - uses refs instead of state
  const acceptRing = useCallback(async () => {
    const ring = incomingRingRef.current;
    if (!ring || isProcessingRef.current) return;

    setIsProcessing(true);
    stopRinging();

    // Add to dismissed list so it won't reopen
    dismissedRingIdsRef.current.add(ring.id);
    console.log('[VideoRing] ✅ Added to dismissed (accepted):', ring.id);

    try {
      // Determine which accept function to call based on video_provider
      const videoProvider = ring.metadata?.video_provider;
      const acceptFn = videoProvider === 'daily' ? 'accept-daily-ring' : 'accept-video-ring';
      console.log('[VideoRing] 📞 Calling', acceptFn, 'for ring:', ring.id, 'provider:', videoProvider);

      const { data, error } = await supabase.functions.invoke(acceptFn, {
        body: { ring_request_id: ring.id },
      });
      console.log('[VideoRing] 📬 Response:', { data, error, hasRoomUrl: !!data?.room_url, hasToken: !!data?.consultant_token });

      if (error) throw error;

      if (videoProvider === 'daily') {
        // For Daily: open embedded DailyVideoCall in CRM with consultant_token
        if (data.room_url && data.consultant_token) {
          setActiveDailyCall({
            roomUrl: data.room_url,
            token: data.consultant_token,
            callId: ring.call_id,
            meetingId: data.meeting_id,
            clientJoinUrl: data.client_join_url,
          });
          toast.success('Videochamada aceita! Abrindo sala...');
        } else {
          console.error('[VideoRing] ❌ Daily data incompleto:', data);
          toast.error('Erro: sala de vídeo não retornada. Tente novamente.');
        }
      } else {
        // For Google Meet: open external link
        toast.success('Call accepted! Opening meeting...');
        if (data.meeting_url) {
          window.open(data.meeting_url, '_blank');
        } else {
          toast.error('Meeting link not available yet');
          console.error('No meeting_url in response:', data);
        }
      }

      setIncomingRing(null);
    } catch (error) {
      console.error('Failed to accept ring:', error);
      toast.error('Failed to accept call');
    } finally {
      setIsProcessing(false);
    }
  }, [stopRinging]);

  // Stable rejectRing - uses refs instead of state
  const rejectRing = useCallback(async () => {
    const ring = incomingRingRef.current;
    if (!ring || isProcessingRef.current) return;

    // IMMEDIATELY add to dismissed list BEFORE anything else
    dismissedRingIdsRef.current.add(ring.id);
    console.log('[VideoRing] ❌ Added to dismissed (rejected):', ring.id);

    setIsProcessing(true);
    stopRinging();
    setIncomingRing(null);

    try {
      await supabase.functions.invoke('reject-video-ring', {
        body: { ring_request_id: ring.id },
      });
      console.log('[VideoRing] Reject edge function called successfully');
    } catch (error) {
      console.error('Failed to reject ring:', error);
      // Don't remove from dismissed list even if edge function fails
    } finally {
      setIsProcessing(false);
    }
  }, [stopRinging]);

  // Combined effect for Realtime subscription and polling
  useEffect(() => {
    console.log('[VideoRing] 🎬 Hook effect running, user:', user?.id, 'isLeader:', isLeader, 'tabId:', tabId.slice(0, 8));
    if (!user) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let pollingInterval: ReturnType<typeof setInterval> | null = null;
    let isMounted = true;

    // Helper to check if a ring was already rejected by current user with cooldown
    const wasRejectedByUser = (ringRequest: any, appUserId: string): boolean => {
      // Check local dismissed list first
      if (dismissedRingIdsRef.current.has(ringRequest.id)) {
        console.log('[VideoRing] 🚫 Ring already in local dismissed list:', ringRequest.id);
        return true;
      }

      // Check metadata.rejections from database with cooldown
      const rejections = ringRequest.metadata?.rejections || [];
      const myRejection = rejections.find((r: any) => r.user_id === appUserId);
      
      if (myRejection) {
        const rejectedAt = new Date(myRejection.rejected_at).getTime();
        const now = Date.now();
        const cooldownRemaining = REJECTION_COOLDOWN_MS - (now - rejectedAt);
        
        if (cooldownRemaining > 0) {
          console.log(`[VideoRing] 🕐 Cooldown ativo: ${Math.round(cooldownRemaining / 1000)}s restantes para ring ${ringRequest.id}`);
          // Also add to local list for consistency
          dismissedRingIdsRef.current.add(ringRequest.id);
          return true;
        } else {
          console.log(`[VideoRing] ⏰ Cooldown expirado para ring ${ringRequest.id}, pode receber novamente`);
        }
      }

      return false;
    };

    // Helper function to fetch full ring request data
    const fetchFullRingRequest = async (ringId: string) => {
      const { data, error } = await supabase
        .from('video_call_ring_requests')
        .select('*')
        .eq('id', ringId)
        .single();
      
      if (error) {
        console.error('[VideoRing] Error fetching full ring request:', error);
        return null;
      }
      
      const metadata = data.metadata as any;
      console.log('[VideoRing] 📥 Re-fetched full ring request:', {
        id: data.id,
        status: data.status,
        current_level: data.current_level,
        rejectionsCount: metadata?.rejections?.length || 0,
      });
      
      return data;
    };

    // Helper function to process a ring request
    const processRingRequest = async (ringRequest: any, appUserId: string) => {
      if (!isMounted) return;
      
      // CRITICAL: Only leader tab should process rings
      if (!isLeader) {
        console.log('[VideoRing] 🔇 Not leader tab, skipping ring processing');
        return;
      }

      // CRITICAL: Ignore rings older than 5 minutes (stale rings)
      const MAX_RING_AGE_MS = 5 * 60 * 1000; // 5 minutes
      const ringCreatedAt = new Date(ringRequest.created_at).getTime();
      const ringAgeMs = Date.now() - ringCreatedAt;
      if (ringAgeMs > MAX_RING_AGE_MS) {
        console.log('[VideoRing] ⏰ Ring muito antigo, ignorando:', ringRequest.id, 'idade:', Math.round(ringAgeMs / 1000), 's');
        return;
      }
      
      // Check if already showing this ring
      if (incomingRingRef.current?.id === ringRequest.id) {
        console.log('[VideoRing] Already showing this ring, skipping');
        return;
      }

      // Check if this ring was already dismissed/rejected by user (with cooldown)
      if (wasRejectedByUser(ringRequest, appUserId)) {
        console.log('[VideoRing] Skipping ring - already rejected by this user (cooldown active)');
        return;
      }

      // Direct call bypass: only ring the specific target user
      const metadata = ringRequest.metadata as Record<string, unknown> | null;
      let ringTimeoutSeconds = 30;

      if (metadata?.is_direct_call) {
        const isDirectTarget = metadata.target_user_id === appUserId;
        console.log('[VideoRing] Direct call detected. Target:', metadata.target_user_id, 'Me:', appUserId, 'Match:', isDirectTarget);
        if (!isDirectTarget) return;
      } else {
        // Standard flow: Get ring settings for current level (filtered by business unit)
        const businessUnit = metadata?.business_unit as string || 'br';
        const { data: settings } = await supabase
          .from('video_call_ring_settings' as any)
          .select('user_ids, timeout_seconds')
          .eq('level', ringRequest.current_level)
          .eq('business_unit', businessUnit)
          .single();

        if (!isMounted) return;

        console.log('[VideoRing] Settings for level', ringRequest.current_level, ':', settings);

        if (!settings) {
          console.log('[VideoRing] No settings found for level', ringRequest.current_level);
          return;
        }

        const isTargeted = (settings as any).user_ids.includes(appUserId);
        console.log('[VideoRing] Am I targeted?', isTargeted, 'My ID:', appUserId, 'Target IDs:', (settings as any).user_ids, 'Level:', ringRequest.current_level);
        
        if (!isTargeted) return;
        ringTimeoutSeconds = (settings as any).timeout_seconds ?? 30;
      }

      // Fetch additional details
      const { data: sdr } = await supabase
        .from('app_users')
        .select('display_name, first_name, last_name')
        .eq('id', ringRequest.requested_by)
        .single();

      if (!isMounted) return;

      let contactName = '';
      let leadName = '';

      if (ringRequest.contact_id) {
        const { data: contact } = await supabase
          .from('contacts')
          .select('first_name, last_name')
          .eq('id', ringRequest.contact_id)
          .single();
        
        if (contact) {
          contactName = `${contact.first_name} ${contact.last_name}`.trim();
        }
      }

      // DEPRECATED - leads lookup removed; lead_id kept for backward compat but not queried

      const { data: call } = await supabase
        .from('calls')
        .select('title')
        .eq('id', ringRequest.call_id)
        .single();

      if (!isMounted) return;

      const incomingRingData: IncomingRing = {
        id: ringRequest.id,
        contact_id: ringRequest.contact_id,
        lead_id: ringRequest.lead_id,
        deal_id: ringRequest.deal_id,
        requested_by: ringRequest.requested_by,
        current_level: ringRequest.current_level,
        call_id: ringRequest.call_id,
        metadata: ringRequest.metadata,
        timeout_seconds: ringTimeoutSeconds,
        sdr_name: sdr?.display_name || `${sdr?.first_name} ${sdr?.last_name}`.trim(),
        contact_name: contactName,
        lead_name: leadName,
        title: call?.title,
      };

      console.log('[VideoRing] 🔔 Showing incoming ring modal:', incomingRingData);
      setIncomingRing(incomingRingData);
      playRingSound();

      // Send browser notification (works even when tab is in background)
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          const sdrDisplay = incomingRingData.sdr_name || 'SDR';
          const contactDisplay = incomingRingData.contact_name || 'Unknown contact';
          notificationRef.current = new Notification('📞 Incoming Video Call', {
            body: `${sdrDisplay} is requesting a video call for ${contactDisplay}`,
            icon: '/favicon.ico',
            requireInteraction: true,
            tag: `video-ring-${ringRequest.id}`,
          });
          notificationRef.current.onclick = () => {
            window.focus();
            notificationRef.current?.close();
          };
        } catch (err) {
          console.error('[VideoRing] Failed to send notification:', err);
        }
      }
    };

    // Get current app user and setup everything
    const setup = async () => {
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!isMounted) return;

      console.log('[VideoRing] 👤 AppUser loaded:', appUser?.id);

      if (!appUser) {
        console.log('[VideoRing] ❌ No appUser found for auth user:', user.id);
        return;
      }

      appUserIdRef.current = appUser.id;

      // Subscribe to ring requests
      channel = supabase
        .channel('video-ring-notifications')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'video_call_ring_requests',
          },
          async (payload) => {
            if (!isMounted) return;
            
            const ringRequest = payload.new as any;
            const oldRingRequest = payload.old as any;
            
            console.log('[VideoRing] 📡 Realtime event:', payload.eventType, {
              id: ringRequest?.id,
              status: ringRequest?.status,
              current_level: ringRequest?.current_level,
              old_level: oldRingRequest?.current_level,
              isLeader,
            });
            
            // Only leader tab processes rings
            if (!isLeader) {
              console.log('[VideoRing] 🔇 Not leader tab, ignoring realtime event');
              return;
            }
            
            if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
              // Check if status changed to accepted/timeout/cancelled
              if (ringRequest.status !== 'ringing') {
                console.log('[VideoRing] Status changed to:', ringRequest.status);
                if (incomingRingRef.current && incomingRingRef.current.id === ringRequest.id) {
                  stopRinging();
                }
                return;
              }

              // Detect escalation - level increased
              if (payload.eventType === 'UPDATE' && oldRingRequest?.current_level && 
                  ringRequest.current_level > oldRingRequest.current_level) {
                console.log('[VideoRing] 🔼 ESCALATION DETECTED! Level changed from', 
                  oldRingRequest.current_level, 'to', ringRequest.current_level);
              }

              // CRITICAL: Re-fetch the full ring request to ensure we have complete metadata
              const fullRingRequest = await fetchFullRingRequest(ringRequest.id);
              if (fullRingRequest) {
                await processRingRequest(fullRingRequest, appUser.id);
              }
            }
          }
        )
        .subscribe((status) => {
          console.log('[VideoRing] 📡 Subscription status:', status);
        });

      // Setup polling as backup - fetch multiple rings
      const pollForActiveRings = async () => {
        if (!isMounted) return;
        
        // Only leader tab should poll
        if (!isLeader) {
          return;
        }
        
        const appUserId = appUserIdRef.current;
        if (!appUserId || incomingRingRef.current) return;

        try {
          // Fetch last 5 active rings from the last 5 minutes only
          const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
          const { data: activeRings } = await supabase
            .from('video_call_ring_requests')
            .select('*')
            .eq('status', 'ringing')
            .gt('created_at', fiveMinutesAgo)
            .order('created_at', { ascending: false })
            .limit(5);

          if (!isMounted || !activeRings || activeRings.length === 0) return;

          console.log('[VideoRing] 🔍 Polling found', activeRings.length, 'active rings');

          // Process rings in order, find first one that targets this user and is not rejected
          for (const ringRequest of activeRings) {
            // Check if already dismissed by this user (with cooldown)
            if (wasRejectedByUser(ringRequest, appUserId)) {
              console.log('[VideoRing] Polling: Skipping ring', ringRequest.id, '- rejected/cooldown');
              continue;
            }

            // Direct call: bypass ring_settings, check target_user_id directly
            const pollMetadata = ringRequest.metadata as Record<string, unknown> | null;
            if (pollMetadata?.is_direct_call) {
              if (pollMetadata.target_user_id === appUserId && !incomingRingRef.current) {
                console.log('[VideoRing] 🚨 Polling: Direct call for me! Ring:', ringRequest.id);
                await processRingRequest(ringRequest, appUserId);
                break;
              }
              continue; // Not for me, skip
            }

            // Standard flow: check ring_settings
            const pollBusinessUnit = pollMetadata?.business_unit as string || 'br';
            const { data: settings } = await supabase
              .from('video_call_ring_settings' as any)
              .select('user_ids, timeout_seconds')
              .eq('level', ringRequest.current_level)
              .eq('business_unit', pollBusinessUnit)
              .single();

            if (!isMounted || !settings) continue;

            const isTargeted = (settings as any).user_ids.includes(appUserId);

            if (isTargeted && !incomingRingRef.current) {
              console.log('[VideoRing] 🚨 Polling detected ring that Realtime missed! Level:', ringRequest.current_level);
              await processRingRequest(ringRequest, appUserId);
              break;
            }
          }
        } catch (err) {
          console.error('[VideoRing] Polling exception:', err);
        }
      };

      // Poll every 10 seconds (reduced from 5s to decrease server load)
      pollingInterval = setInterval(pollForActiveRings, 10000);
      // Also poll once after setup
      setTimeout(pollForActiveRings, 1000);
    };

    setup();

    return () => {
      isMounted = false;
      console.log('[VideoRing] 🧹 Cleaning up');
      if (channel) {
        supabase.removeChannel(channel);
      }
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      // Clean up ring sound interval
      if (ringIntervalRef.current) {
        clearInterval(ringIntervalRef.current);
        ringIntervalRef.current = null;
      }
    };
  }, [user, isLeader, tabId, stopRinging, playRingSound]);

  const clearActiveDailyCall = useCallback(() => {
    setActiveDailyCall(null);
  }, []);

  const openDailyCall = useCallback((call: ActiveDailyCall) => {
    setActiveDailyCall(call);
  }, []);

  return {
    incomingRing,
    acceptRing,
    rejectRing,
    isProcessing,
    activeDailyCall,
    clearActiveDailyCall,
    openDailyCall,
  };
}
